<template>
	<div class="wallet-card" :class="{ 'wallet-card--portfolio': header === 'token' }">
		<!-- Wallet Balance Card -->
		<div v-if="!header" class="wallet-card__content">
			<div class="wallet-card__header">
				<div class="currency-selector">
					<CurrencyFlag
						v-if="selectedCurrencyLocal"
						:currency="selectedCurrencyLocal"
					/>
					<select
						name="currency"
						id="currency"
						v-model="selectedCurrencyLocal"
						class="currency-select"
					>
						<option :value="wallet.currency" :key="wallet.id" v-for="wallet in walletBalance">
							{{ wallet.currency }} Balance
						</option>
					</select>
					<svg class="select-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
						<path d="M6 8L2 4H10L6 8Z" fill="#94A3B8"/>
					</svg>
				</div>
				<span class="wallet-label">Available Balance</span>
			</div>
			<div class="wallet-card__body">
				<p class="wallet-balance">
					{{
						activeWallet
							? formatAmountToDollar(
									activeWallet.balance,
									2,
									activeWallet.currency
								)
							: formatAmountToDollar(0, 2, selectedCurrencyLocal || "USD")
					}}
				</p>
				<div class="wallet-trend wallet-trend--up">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M8 4L12 8H9V12H7V8H4L8 4Z" fill="currentColor"/>
					</svg>
					<span>+2.4%</span>
				</div>
			</div>
			<div class="wallet-card__actions">
				<button class="btn-primary" @click="$emit('fund-wallet')">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
					</svg>
					Fund Wallet
				</button>
				<button class="btn-secondary">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M8 13V3M3 8L8 3L13 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					Withdraw
				</button>
			</div>
		</div>

		<!-- Portfolio Card -->
		<div v-if="header === 'token'" class="portfolio-card__content">
			<div class="portfolio-card__header">
				<div class="portfolio-icon">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
						<path d="M3.89701 17.86L7.80701 13.95L10.636 16.778L15.207 12.208L17 14V9H12L13.793 10.793L10.636 13.95L7.80801 11.121L2.86201 16.067C2.29194 14.787 1.99821 13.4012 2.00001 12C2.00001 6.477 6.47701 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C10.418 22.001 8.85839 21.6262 7.44962 20.9065C6.04084 20.1867 4.82321 19.1425 3.89701 17.86Z" fill="#4D84FF"/>
					</svg>
				</div>
				<span class="portfolio-label">Your Portfolio</span>
			</div>
			
			<template v-if="tokens?.length === 0">
				<div class="portfolio-empty">
					<div class="portfolio-empty__icon">
						<svg width="40" height="40" viewBox="0 0 24 24" fill="none">
							<path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#CBD5E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M2 17L12 22L22 17" stroke="#CBD5E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M2 12L12 17L22 12" stroke="#CBD5E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
					</div>
					<p class="portfolio-empty__text">No shares yet</p>
					<p class="portfolio-empty__subtext">Start building your portfolio</p>
					<button 
						class="btn-primary btn-primary--small"
						@click="$router.push({ name: 'Trade', params: { option: 'Now Raising' } })"
					>
						Buy Shares
					</button>
				</div>
			</template>
			
			<template v-else>
				<div class="portfolio-list">
					<div class="portfolio-avatars">
						<div 
							v-for="(token, index) in tokens?.slice(0, 4)" 
							:key="index"
							class="portfolio-avatar"
							:style="{ zIndex: 4 - index }"
						>
							<img :src="token?.image" :alt="token?.name" />
						</div>
						<div v-if="tokens?.length > 4" class="portfolio-avatar portfolio-avatar--more">
							+{{ tokens.length - 4 }}
						</div>
					</div>
					<div class="portfolio-summary">
						<span class="portfolio-count">{{ tokens?.length }} Assets</span>
						<router-link 
							class="portfolio-link"
							:to="{ name: 'Wallet', query: { tab: 'investments' } }"
						>
							View all
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
						</router-link>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup>
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
import { formatAmountToDollar } from "@/utils/helpers.js";
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

defineEmits(["fund-wallet"]);

const store = useStore()

const props = defineProps({
	header: {
		type: String,
		default: () => "",
	},
	walletBalance: {
		type: Array,
		default: () => [],
	},
	tokens: {
		type: Array,
		default: () => [],
	},
	selectedCurrency: {
		type: String,
		default: () => "",
	},
});

const walletBalance = computed(() => props.walletBalance);

const selectedWallet = ref(null);
const selectedCurrencyLocal = ref("");
const activeWallet = computed(() => {
	if (selectedWallet.value) {
		return selectedWallet.value;
	}
	return walletBalance.value?.[0] || null;
});

// Sync with parent's selected currency
watch(
	() => props.selectedCurrency,
	(newVal) => {
		if (newVal && walletBalance.value?.length > 0) {
			const wallet = walletBalance.value.find((w) => w.currency === newVal);
			if (wallet) {
				selectedWallet.value = wallet;
				selectedCurrencyLocal.value = newVal;
			}
		}
	},
	{ immediate: true }
);

onMounted(() => {
	if (walletBalance.value && walletBalance.value.length > 0) {
		// Use parent's selected currency if provided
		if (props.selectedCurrency) {
			const wallet = walletBalance.value.find((w) => w.currency === props.selectedCurrency);
			selectedWallet.value = wallet || walletBalance.value[0];
		} else {
			selectedWallet.value = walletBalance.value[0];
		}
		if (selectedWallet.value) {
			selectedCurrencyLocal.value = selectedWallet.value.currency;
			store.dispatch('organisationModule/setCurrency', selectedWallet.value.currency);
		}
	}
});

watch(walletBalance, (newVal, oldVal) => {
	if (Array.isArray(newVal) && newVal.length > 0) {
		const currentCurrency = selectedCurrencyLocal.value || selectedWallet.value?.currency;
		const matched = currentCurrency
			? newVal.find((wallet) => wallet.currency === currentCurrency)
			: null;
		selectedWallet.value = matched || newVal[0];
		selectedCurrencyLocal.value = selectedWallet.value.currency;
		store.dispatch('organisationModule/setCurrency', selectedWallet.value.currency);
	}
});
watch(selectedCurrencyLocal, (newVal, oldVal) => {
  if (newVal) {
    store.dispatch('organisationModule/setCurrency', newVal)
		if(newVal !== selectedWallet.value?.currency) {
			selectedWallet.value = walletBalance.value.find((wallet) => wallet.currency === newVal) || selectedWallet.value
		}
  }
})
watch(selectedWallet, (newVal, oldVal) => {
  if (newVal && newVal !== selectedCurrencyLocal.value) {
		selectedCurrencyLocal.value = newVal.currency
  }
})
</script>

<style lang="scss" scoped>
.wallet-card {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;

	&--portfolio {
		display: flex;
		flex-direction: column;
	}
}

.wallet-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
}

.currency-selector {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		border-color: #cbd5e1;
	}
}

.currency-select {
	appearance: none;
	background: transparent;
	border: none;
	font-size: 14px;
	font-weight: 400;
	color: #0f172a;
	cursor: pointer;
	padding-right: 16px;

	&:focus {
		outline: none;
	}
}

.select-chevron {
	margin-left: -12px;
}

.wallet-label {
	font-size: 13px;
	color: #94a3b8;
	font-weight: 400;
}

.wallet-card__body {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin-bottom: 24px;
}

.wallet-balance {
	font-size: 32px;
	font-weight: 500;
	color: #0f172a;
	letter-spacing: -0.5px;
}

.wallet-trend {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 10px;
	border-radius: 6px;
	font-size: 13px;
	font-weight: 500;

	&--up {
		background: rgba(16, 185, 129, 0.1);
		color: #10b981;
	}

	&--down {
		background: rgba(239, 68, 68, 0.1);
		color: #ef4444;
	}
}

.wallet-card__actions {
	display: flex;
	gap: 12px;
}

.btn-primary {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	flex: 1;
	padding: 14px 20px;
	background: #0f172a;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: #1e293b;
		box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
	}

	&--small {
		flex: none;
		padding: 10px 16px;
		font-size: 13px;
	}
}

.btn-secondary {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	flex: 1;
	padding: 14px 20px;
	background: transparent;
	color: #0f172a;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: #f8fafc;
		border-color: #cbd5e1;
	}
}

// Portfolio Card Styles
.portfolio-card__content {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.portfolio-card__header {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 20px;
}

.portfolio-icon {
	width: 40px;
	height: 40px;
	background: rgba(77, 132, 255, 0.1);
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.portfolio-label {
	font-size: 15px;
	font-weight: 500;
	color: #0f172a;
}

.portfolio-empty {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 20px;
}

.portfolio-empty__icon {
	width: 64px;
	height: 64px;
	background: #f8fafc;
	border-radius: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;
}

.portfolio-empty__text {
	font-size: 15px;
	font-weight: 500;
	color: #0f172a;
	margin-bottom: 4px;
}

.portfolio-empty__subtext {
	font-size: 13px;
	color: #94a3b8;
	margin-bottom: 16px;
}

.portfolio-list {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
}

.portfolio-avatars {
	display: flex;
	margin-bottom: 16px;
}

.portfolio-avatar {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	overflow: hidden;
	border: 3px solid #ffffff;
	margin-left: -12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

	&:first-child {
		margin-left: 0;
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&--more {
		background: #f1f5f9;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		font-weight: 500;
		color: #475569;
	}
}

.portfolio-summary {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.portfolio-count {
	font-size: 14px;
	font-weight: 400;
	color: #475569;
}

.portfolio-link {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 14px;
	font-weight: 500;
	color: #4d84ff;
	text-decoration: none;
	transition: color 0.2s ease;

	&:hover {
		color: #3b6de0;
	}
}
</style>
